import { useCallback, useState } from "react";

import {
  Button,
  InfoBar,
  Link,
  PageContainer,
  SpinnerCenter,
  useRemoteConfig,
} from "@shapr3d/frontend-lib";

import CTARow from "../components/CTARow/CTARow";
import { CTARowDivider } from "../components/CTARowDivider/CTARowDivider";
import DeleteAccountModal from "../components/DeleteAccountModal/DeleteAccountModal";
import EmailChangeModal from "../components/EmailChangeModal/EmailChangeModal";
import LinkPersonalAccountModal from "../components/LinkPersonalAccountModal/LinkPersonalAccountModal";
import NameChangeModal from "../components/NameChangeModal/NameChangeModal";
import PasswordChangeModal from "../components/PasswordChangeModal/PasswordChangeModal";
import RecoveryCodeModal from "../components/RecoveryCodeModal/RecoveryCodeModal";
import SetupTwoFactorModal from "../components/SetupTwoFactorModal/SetupTwoFactorModal";
import { formatExpirationDate } from "../components/TeamSubscriptions/subscriptionUtils";
import TwoFactorDisableModal from "../components/TwoFactorDisableModal/TwoFactorDisableModal";
import TwoFactorEnabledModal from "../components/TwoFactorEnabledModal/TwoFactorEnabledModal";
import UnderMaintenanceOrChildren from "../components/UnderMaintenanceOrChildren/UnderMaintenanceOrChildren";
import useAccount from "../hooks/useAccount";
import { useResendVerificationEmail } from "../hooks/useAuth";
import { useTracker } from "../hooks/useTracker";
import useTranslation from "../hooks/useTranslation";
import { ElementClickAction } from "../lib/TrackingAdapter";

import style from "./AccountPage.module.css";

const AccountPage = () => {
  const { t, m } = useTranslation();
  const tracker = useTracker();
  const { data: accountData, isLoading: isAccountLoading } = useAccount();
  const isLinkPersonalAccountEnabled = useRemoteConfig("web_link_personal_account-ff");

  const {
    mutate: resendVerificationEmail,
    isPending: isResendingVerification,
    isSuccess: isResendVerificationSuccess,
  } = useResendVerificationEmail();

  const [isNameChangeModalOpen, setIsNameChangeModalOpen] = useState(false);
  const [isEmailChangeModalOpen, setIsEmailChangeModalOpen] = useState(false);
  const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = useState(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [isSetupTwoFactorModalOpen, setIsSetupTwoFactorModalOpen] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [isRecoveryCodeModalOpen, setIsRecoveryCodeModalOpen] = useState(false);
  const [isTwoFactorEnabledModalOpen, setIsTwoFactorEnabledModalOpen] = useState(false);
  const [isTwoFactorDisableModalOpen, setIsTwoFactorDisableModalOpen] = useState(false);
  const [isLinkPersonalAccountModalOpen, setIsLinkPersonalAccountModalOpen] = useState(false);

  const handleResendVerificationEmail = useCallback(() => {
    tracker.trackRequestVerificationEmailClicked({ location: "2fa_enable" });
    resendVerificationEmail();
  }, [resendVerificationEmail, tracker]);

  if (isAccountLoading || !accountData) {
    return (
      <PageContainer>
        <SpinnerCenter />
      </PageContainer>
    );
  }

  return (
    <UnderMaintenanceOrChildren>
      <>
        <PageContainer title={t(m.Account.Title)}>
          <div className={style.container}>
            <CTARowDivider variant="bottom" />
            <CTARow
              title={t(m.Account.YourName)}
              text={<div className={style.userInputText}>{accountData.name ?? "-"}</div>}
              CTA={
                <Button
                  variant="borderless"
                  onClick={() => {
                    setIsNameChangeModalOpen(true);
                    tracker.trackButtonClick({
                      action: ElementClickAction.CHANGE_NAME,
                    });
                  }}
                >
                  {t(m.Account.Edit)}
                </Button>
              }
            />
            <CTARowDivider />
            <CTARow
              title={t(m.Account.Email)}
              text={
                <div>
                  {accountData.ssoEnforced && (
                    <div className={style.ssoText}>{t(m.Account.SsoEmailControlled)}</div>
                  )}
                  <div className={style.userInputText}>{accountData.email}</div>
                </div>
              }
              CTA={
                !accountData.ssoEnforced && (
                  <Button
                    variant="borderless"
                    onClick={() => {
                      setIsEmailChangeModalOpen(true);
                      tracker.trackButtonClick({
                        elementId: "open-email-change-button",
                        action: ElementClickAction.CHANGE_EMAIL,
                      });
                    }}
                  >
                    {t(m.Account.Edit)}
                  </Button>
                )
              }
            />
            <CTARowDivider />
            <CTARow
              title={t(m.Account.Password)}
              text={
                <div>
                  {accountData.ssoEnforced && (
                    <div className={style.ssoText}>{t(m.Account.SsoPasswordControlled)}</div>
                  )}
                  <span>*********</span>
                </div>
              }
              CTA={
                !accountData.ssoEnforced && (
                  <Button
                    variant="borderless"
                    onClick={() => {
                      setIsPasswordChangeModalOpen(true);
                      tracker.trackButtonClick({
                        action: ElementClickAction.CHANGE_PASSWORD,
                      });
                    }}
                  >
                    {t(m.Account.Edit)}
                  </Button>
                )
              }
            />
            <CTARowDivider />
            <CTARow
              title={t(m.Account.TwoFactor.Title)}
              text={
                <div>
                  {accountData.ssoEnforced && <div>{t(m.Account.TwoFactor.SsoControlled)}</div>}
                  {!accountData.ssoEnforced && (
                    <>
                      {accountData.twoFactorTotpEnabled && (
                        <div>{t(m.Account.TwoFactor.Enabled)}</div>
                      )}

                      {!accountData.twoFactorTotpEnabled && (
                        <div>{t(m.Account.TwoFactor.Disabled)}</div>
                      )}
                    </>
                  )}
                </div>
              }
              CTA={
                !accountData.ssoEnforced && (
                  <>
                    {accountData.twoFactorTotpEnabled && (
                      <Button
                        variant="borderless"
                        className={style.danger}
                        onClick={() => {
                          setIsTwoFactorDisableModalOpen(true);
                        }}
                      >
                        {t(m.Account.TwoFactor.Disable)}
                      </Button>
                    )}
                    {!accountData.twoFactorTotpEnabled && !accountData.emailVerified && (
                      <InfoBar
                        variant="warning"
                        text={
                          <div>
                            {t(m.Account.TwoFactor.VerifyEmail)}
                            <br />
                            {isResendVerificationSuccess ? (
                              <span>{t(m.Account.TwoFactor.VerificationSent)}</span>
                            ) : (
                              <>
                                {isResendingVerification ? (
                                  <span>{t(m.Account.TwoFactor.Sending)}</span>
                                ) : (
                                  <Link onClick={handleResendVerificationEmail}>
                                    {t(m.Account.TwoFactor.ResendVerification)}
                                  </Link>
                                )}
                              </>
                            )}
                          </div>
                        }
                      />
                    )}

                    {!accountData.twoFactorTotpEnabled && accountData.emailVerified && (
                      <Button
                        variant="borderless"
                        onClick={() => {
                          setIsSetupTwoFactorModalOpen(true);
                        }}
                      >
                        {t(m.Account.TwoFactor.Enable)}
                      </Button>
                    )}
                  </>
                )
              }
            />
            {isLinkPersonalAccountEnabled && accountData.showPersonalAccountSection && (
              <>
                <CTARowDivider />
                <CTARow
                  title={t(m.Account.LinkedPersonalAccount)}
                  text={
                    accountData.linkedPersonalAccount ? (
                      <div className={style.userInputText}>
                        {accountData.linkedPersonalAccount.email}
                        {accountData.linkedPersonalAccount.giftIsActive &&
                          new Date(accountData.linkedPersonalAccount.giftExpirationDate) >
                            new Date() && (
                            <>
                              {" "}
                              (
                              {t(m.Account.GiftExpires, {
                                date: formatExpirationDate(
                                  accountData.linkedPersonalAccount.giftExpirationDate
                                ),
                              })}
                              )
                            </>
                          )}
                      </div>
                    ) : (
                      <div className={style.helperText}>{t(m.Account.LinkPersonalEmail)}</div>
                    )
                  }
                  CTA={
                    accountData.canLinkPersonalAccount ? (
                      <Button
                        variant="borderless"
                        onClick={() => {
                          setIsLinkPersonalAccountModalOpen(true);
                          tracker.trackButtonClick({
                            action: ElementClickAction.LINK_PERSONAL_ACCOUNT,
                          });
                        }}
                      >
                        {t(m.Account.LinkAccount)}
                      </Button>
                    ) : null
                  }
                />
              </>
            )}
            <CTARow
              className={style.deleteAccount}
              title={t(m.Account.DeleteAccount)}
              text={t(m.Account.PermanentlyDelete)}
              CTA={
                <Button
                  variant="borderless"
                  className={style.danger}
                  onClick={() => {
                    setIsDeleteAccountModalOpen(true);
                    tracker.trackButtonClick({
                      elementId: "open-delete-account-button",
                      action: ElementClickAction.DELETE_ACCOUNT_MODAL_OPENED,
                    });
                  }}
                >
                  {t(m.Account.DeleteAccount)}
                </Button>
              }
            />
          </div>
        </PageContainer>

        <NameChangeModal
          onClose={() => setIsNameChangeModalOpen(false)}
          name={accountData.name}
          open={isNameChangeModalOpen}
        ></NameChangeModal>

        <EmailChangeModal
          onClose={() => setIsEmailChangeModalOpen(false)}
          open={isEmailChangeModalOpen && !accountData.ssoEnforced}
        />
        <PasswordChangeModal
          onClose={() => setIsPasswordChangeModalOpen(false)}
          open={isPasswordChangeModalOpen && !accountData.ssoEnforced}
        ></PasswordChangeModal>
        <DeleteAccountModal
          onClose={() => setIsDeleteAccountModalOpen(false)}
          open={isDeleteAccountModalOpen}
        />

        <SetupTwoFactorModal
          open={isSetupTwoFactorModalOpen}
          onClose={() => setIsSetupTwoFactorModalOpen(false)}
          onSuccess={(code) => {
            setRecoveryCode(code);
            setIsSetupTwoFactorModalOpen(false);
            setIsRecoveryCodeModalOpen(true);
          }}
        />

        <RecoveryCodeModal
          open={isRecoveryCodeModalOpen && Boolean(recoveryCode)}
          recoveryCode={recoveryCode ?? ""}
          onClose={() => {
            setRecoveryCode(null);
            setIsRecoveryCodeModalOpen(false);
            setIsTwoFactorEnabledModalOpen(true);
          }}
        />

        <TwoFactorEnabledModal
          open={isTwoFactorEnabledModalOpen}
          onClose={() => setIsTwoFactorEnabledModalOpen(false)}
        />

        <TwoFactorDisableModal
          open={isTwoFactorDisableModalOpen}
          onClose={() => setIsTwoFactorDisableModalOpen(false)}
        />

        <LinkPersonalAccountModal
          open={isLinkPersonalAccountModalOpen}
          onClose={() => setIsLinkPersonalAccountModalOpen(false)}
          currentEmail={accountData.email}
          salesTeams={accountData.salesTeams}
        />
      </>
    </UnderMaintenanceOrChildren>
  );
};

export default AccountPage;
